import { Component, Event, type EventEmitter, Prop, h } from '@stencil/core';
import type { ConversationalSearchMessages } from '../../../conversational-search-types/conversational-search-types.type';
import type { labelProviderInterface } from '../../../../../LabelProviderInterface';
import { answerViewTypes, getAllByType, getByType, typeIdentifier } from '../../../conversational-search-logic/utils/utils';
import { getComponentToRender, type RenderingList } from './dynamic-mediator-helpers';
import { ConversationalSearchProductHierarchyWrapper } from '../../messages-dynamic/product-hierarchy/product-hierarchy-wrapper';
import { ConversationalSearchMessagesSuggestionsWrapper } from '../../messages-dynamic/suggestions/suggestions-wrapper/suggestions-wrapper';
import { ConversationalSearchMessagesErrorAnswerFallback } from '../../predefined/error-answer-fallback/error-answer-fallback';
import { ConversationalSearchFollowUpNotSatisfied } from '../../predefined/follow-ups/follow-up-not-satisfied/follow-up-not-satisfied';
import { ConversationalSearchFollowUpSatisfied } from '../../predefined/follow-ups/follow-up-satisfied/follow-up-satisfied';
import { ConversationalSearchNotDetailedAnswerFallback } from '../../predefined/not-detailed-answer-fallback/not-detailed-answer-fallback';
import { ConversationalSearchNotDetailedAnswer } from '../../predefined/not-detailed-answer/not-detailed-answer';
import { ConversationalSearchUserMessage } from '../../predefined/user-message/user-message';

@Component({
  tag: 'uiaas-search-widget-conversational-search-messages-dynamic-mediator',
  styleUrl: 'dynamic-mediator.scss',
  shadow: false,
})
export class ConversationalSearchMessagesDynamicMediator {
  @Prop() readonly conversationalSearchMedaliaFormId: string;
  @Prop() readonly conversationalSearchBrandUrl: string;
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly data: ConversationalSearchMessages;
  @Prop() readonly query: string;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly isFollowUpBlocked: boolean;

  // It is required to have hierarchy and suggestions
  // as a property of the component to be able to
  // pass it to getComponentToRender function
  private hierarchy;
  private suggestions;
  private renderFollowUpMediator;
  private DynamicComponent;

  @Event({
    eventName: 'conversational-search:ga-result-suggestions',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    gaResultSuggestionEventEmitter: EventEmitter<{
    suggestions: ConversationalSearchMessages['results']
  }>;

  @Event({
    eventName: 'conversational-search:show',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    gaShowEventEmitter: EventEmitter<{ keyword: string, category: string }>;

  // ComponentType F - Functional
  // ComponentType W - WebComponent
  private viewsTable: RenderingList[] = [
    { type: answerViewTypes.UserMessage, component: ConversationalSearchUserMessage, componentType: 'F', props: ['action', 'query'] },
    { type: answerViewTypes.ErrorMessage, component: ConversationalSearchMessagesErrorAnswerFallback, componentType: 'F', props: ['labelProvider', 'country', 'language', 'query'] },
    { type: answerViewTypes.FollowUpSatisfied, component: ConversationalSearchFollowUpSatisfied, componentType: 'F', props: ['labelProvider', 'query'] },
    { type: answerViewTypes.FollowUpNotSatisfied, component: ConversationalSearchFollowUpNotSatisfied, componentType: 'F', props: ['labelProvider', 'country', 'language', 'query', 'conversationalSearchMedaliaFormId'] },
    { type: answerViewTypes.SuggestionResults, component: ConversationalSearchMessagesSuggestionsWrapper, componentType: 'F', props: ['labelProvider', 'country', 'language', 'query', 'conversationalSearchBrandUrl', 'suggestions', 'hierarchy'], checkCallback: () => this.suggestions?.length },
    { type: answerViewTypes.HierarchyResults, component: ConversationalSearchProductHierarchyWrapper, componentType: 'F', props: ['labelProvider', 'query', 'hierarchy', 'language'], checkCallback: () => Boolean(this.hierarchy) },
    { type: answerViewTypes.Irrelevance, component: 'uiaas-conversational-search-message-not-offer-related', componentType: 'W', props: ['labelProvider', 'country', 'language', 'query'], checkCallback: () => typeIdentifier(this.data?.followups, answerViewTypes.Irrelevance) },
    { type: answerViewTypes.Categories, component: ConversationalSearchNotDetailedAnswer, componentType: 'F', props: ['labelProvider', 'data', 'query', 'targetFollowUp'], checkCallback: () => typeIdentifier(this.data?.followups, answerViewTypes.Categories) },
    { type: answerViewTypes.NotDetailedAnswer, component: ConversationalSearchNotDetailedAnswerFallback, componentType: 'F', props: ['labelProvider', 'query'], checkCallback: () => !typeIdentifier(this.data?.followups, answerViewTypes.Categories) },
    { type: answerViewTypes.Fallback, component: ConversationalSearchMessagesErrorAnswerFallback, componentType: 'F', props: ['labelProvider', 'country', 'language', 'query'] },
  ];

  async componentWillLoad() {
      this.hierarchy = getByType(this.data?.results, answerViewTypes.HierarchyResults);
      this.suggestions = getAllByType(this.data?.results, answerViewTypes.SuggestionResults);
      this.renderFollowUpMediator = this.suggestions?.length || this.hierarchy;
      this.DynamicComponent = await getComponentToRender(this, this.viewsTable, this.data?.type, answerViewTypes.Fallback);
      this.sendGaEvenets();
  }

  private sendGaEvenets = () => {
      if (this.suggestions?.length) this.gaResultSuggestionEventEmitter.emit({ suggestions: this.suggestions });
      if (this.hierarchy) {
        const category = this.hierarchy?.categoryL2;
        const eventCategory = category ? `${category} Hierarchy` : 'GenAI Product Hierarchy';
        this.gaShowEventEmitter.emit({ keyword: this.query, category: eventCategory });
      }
  };

  render() {
      return (
        <section>
          <uiaas-search-conversational-search-messages-wrapper
            isQuestionVariant={this.data.type === answerViewTypes.UserMessage}
            areActionsVisible={this.renderFollowUpMediator && !this.data?.isFollowupRefine}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            query={this.query}
          >
            {this.DynamicComponent}
          </uiaas-search-conversational-search-messages-wrapper>
          {this.renderFollowUpMediator && !this.isFollowUpBlocked && (
            <uiaas-conversational-search-message-follow-up-mediator
              labelProvider={(e, f) => this.labelProvider(e, f)}
              query={this.query}
              hierarchy={this.hierarchy}
              suggestions={this.suggestions}
              followups={this.data?.followups}
              conversationalSearchBrandUrl={this.conversationalSearchBrandUrl}
              country={this.country}
              language={this.language}
            />
          )}
        </section>
      );
  }
}
